import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";

// Multi-brand by domain: the visitor's hostname decides which brand (Fidel / JT / …)
// the public site + portal render. The backend resolves the host → company.
export const BRAND_HOST = typeof window !== "undefined" ? window.location.hostname : "";
export const BRAND_LOGO_URL = `${API_BASE}/public/brand/logo?host=${encodeURIComponent(BRAND_HOST)}`;

let _cache;
let _promise;
let _gaLoadedFor; // the measurement ID already injected, so a second brand fetch never double-loads it

// Loads GA4 (gtag.js) for this brand's own Measurement ID, once. jtshipping.in and
// fidellogistics.in are separate businesses sharing one build, so each company (or
// the default site settings) carries its own ID — see CompanyIn.ga_measurement_id /
// SiteSettingsIn.ga_measurement_id on the backend — and we load whichever one this
// host resolved to, never both.
function loadGoogleAnalytics(measurementId) {
  if (!measurementId || _gaLoadedFor === measurementId || typeof document === "undefined") return;
  _gaLoadedFor = measurementId;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  window.gtag("js", new Date());
  window.gtag("config", measurementId);
}

export function fetchBrand() {
  if (_cache) return Promise.resolve(_cache);
  if (_promise) return _promise;
  _promise = api.get("/public/brand", { params: { host: BRAND_HOST } })
    .then(({ data }) => {
      _cache = data;
      loadGoogleAnalytics(data?.ga_measurement_id);
      return data;
    })
    .catch(() => ({ is_default: true, name: "FIDEL GROUP" }));
  return _promise;
}

/** Brand identity for the current domain (logo, name, contact, colour). */
export function useBrand() {
  const [brand, setBrand] = useState(_cache || null);
  useEffect(() => {
    let active = true;
    fetchBrand().then((b) => {
      if (!active) return;
      setBrand(b);
      // Reflect the brand in the tab title (best-effort).
      if (b?.name && typeof document !== "undefined") document.title = b.name;
    });
    return () => { active = false; };
  }, []);
  return brand;
}
